import React, { useEffect, useState } from 'react';
import { Cloud, HardDrive, Loader2, Check, ExternalLink } from 'lucide-react';
import {
  getConversationStorageStatus, startConversationDriveMigration, revertConversationToCloud,
  type StorageStatus,
} from '../../services/conversationService';
import { connectDrive } from '../../services/driveService';
import type { ChatSession } from './types';
import { TYPE } from '../../constants/typeScale';

/**
 * Storage control — move a chat between Wisetree Cloud and the owner's Google Drive. When
 * Drive-backed, the chat's messages (and media) live in the user's Drive; our system keeps
 * the account, wallet, and chat metadata. Connect Drive on demand (never at login).
 */
const ChatStorageSection: React.FC<{ session: ChatSession }> = ({ session }) => {
  const [status, setStatus] = useState<StorageStatus | null>(null);
  const [busy, setBusy] = useState<'' | 'migrate' | 'revert'>('');
  const [error, setError] = useState('');

  useEffect(() => {
    getConversationStorageStatus(session.id).then(setStatus).catch(() => setStatus({
      storage_location: session.storageLocation || 'cloud', migration_status: 'none', drive_folder_id: null,
    }));
  }, [session.id, session.storageLocation]);

  const moveToDrive = async () => {
    setBusy('migrate'); setError('');
    try {
      setStatus(await startConversationDriveMigration(session.id));
    } catch (e: any) {
      // Not connected yet → run the on-demand Drive consent popup, then retry once.
      if (/connect google drive/i.test(e?.message || '')) {
        try {
          const ok = await connectDrive();
          if (!ok) { setError('Google Drive not connected.'); return; }
          setStatus(await startConversationDriveMigration(session.id));
        } catch (e2: any) { setError(e2?.message || 'Could not connect Google Drive'); }
      } else { setError(e?.message || 'Could not move this chat to Drive'); }
    } finally { setBusy(''); }
  };

  const moveToCloud = async () => {
    setBusy('revert'); setError('');
    try { setStatus(await revertConversationToCloud(session.id)); }
    catch (e: any) { setError(e?.message || 'Could not move this chat back to cloud'); }
    finally { setBusy(''); }
  };

  const st = status?.migration_status || 'none';
  const onDrive = status?.storage_location === 'drive' && st === 'active';
  const inFlight = st === 'migrating' || st === 'reverting' || !!busy;

  return (
    <section>
      <p className={`mb-2 ${TYPE.label} text-secondary-400`}>Where this chat is stored</p>

      {inFlight ? (
        <div className={`flex items-center gap-2 rounded-lg border border-secondary-200 dark:border-secondary-600 px-3 py-2 ${TYPE.body} text-secondary-600 dark:text-secondary-300`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          {st === 'reverting' || busy === 'revert' ? 'Moving back to Wisetree Cloud…' : 'Moving this chat to your Google Drive…'}
        </div>
      ) : onDrive ? (
        <>
          <div className={`flex items-center gap-2 rounded-lg border border-secondary-900 dark:border-white px-3 py-2 ${TYPE.body} text-secondary-900 dark:text-white`}>
            <Check className="h-4 w-4" /> Stored in your Google Drive
          </div>
          <div className="mt-2 flex items-center gap-3">
            <button onClick={moveToCloud} className={`${TYPE.control} text-secondary-500 hover:text-secondary-900 dark:hover:text-white inline-flex items-center gap-1`}>
              <Cloud className="h-3.5 w-3.5" /> Move back to Wisetree Cloud
            </button>
            {status?.drive_folder_id && (
              <a href={`https://drive.google.com/drive/folders/${status.drive_folder_id}`} target="_blank" rel="noreferrer"
                className={`${TYPE.control} text-secondary-500 hover:text-secondary-900 dark:hover:text-white inline-flex items-center gap-1`}>
                <ExternalLink className="h-3.5 w-3.5" /> Open folder
              </a>
            )}
          </div>
        </>
      ) : (
        <div className="flex gap-2">
          <span className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-secondary-900 dark:border-white px-3 py-2 ${TYPE.body} text-secondary-900 dark:text-white`}>
            <Cloud className="h-4 w-4" /> Wisetree Cloud
          </span>
          <button onClick={moveToDrive}
            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-secondary-300 dark:border-secondary-600 px-3 py-2 ${TYPE.body} text-secondary-600 dark:text-secondary-300 hover:border-secondary-500`}>
            <HardDrive className="h-4 w-4" /> Move to Google Drive
          </button>
        </div>
      )}

      {error && <p className={`mt-1.5 ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>{error}</p>}
      <p className={`mt-1.5 ${TYPE.control} text-secondary-400`}>
        {onDrive
          ? "This chat's messages and files live in your Google Drive — you own them. We keep your account, wallet, and chat list."
          : 'Stored in Wisetree Cloud — recommended for most chats (faster, searchable, and your agents work best here). For a sensitive or private chat, move it to your own Google Drive for full ownership; we ask for Drive access only then, never at login.'}
      </p>
    </section>
  );
};

export default ChatStorageSection;
